// ─────────────────────────────────────────────────────────────────────
// <HomePage /> — design vision composed
//
// Anatomy:
//   1. HeroCarousel  — featured + trending + boosted (5 slots)
//   2. ChipFilterRow — quick filters (sticky on mobile)
//   3. EventRail     — Trending in <city>
//   4. SponsoredStrip— paid promo banner
//   5. Vertical tiles— 6 verticals in editorial 2x3 grid
//   6. EventRail     — Hand-picked / curated
//   7. Mood tiles    — 22 moods in editorial grid
//   8. Ask panel     — AI search prompt
// ─────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import api from '../api/client';
import { useAuth } from '../context/AuthContext';   // r7.2
import { useLocation as useCity } from '../context/LocationContext';

import HeroCarousel    from '../components/event/HeroCarousel';
import EventRail       from '../components/event/EventRail';
import SponsoredStrip  from '../components/event/SponsoredStrip';
import ChipFilterRow   from '../components/common/ChipFilterRow';
import SMark           from '../components/ai/SMark';
import { useCategories } from '../lib/taxonomy';                 // r7.6.4
import { CategoryCard } from '../components/discovery/CategoryCard';
import { adaptHeroSlides } from '../lib/heroSlides';   // r7.7 C4: the placements feed → carousel slides

const VERTICALS = [
  { slug: 'live-events', label: 'Live Events', glyph: '✦', variant: 'gold',  sub: 'Concerts · Comedy · Indie' },
  { slug: 'sports',      label: 'Sports',      glyph: '◯', variant: 'sage',  sub: 'Cricket · Football · Courts' },
  { slug: 'theatre',     label: 'Plays',       glyph: 'थ', variant: 'rose',  sub: 'Theatre · Drama · Storytelling' },
  { slug: 'attractions', label: 'Attractions', glyph: '⌖', variant: 'slate', sub: 'Museums · Parks · Slot entry' },
  { slug: 'workshops',   label: 'Workshops',   glyph: '✎', variant: 'plum',  sub: 'Pottery · Photography · Cooking' },
  { slug: 'spiritual',   label: 'Spiritual',   glyph: 'ॐ', variant: 'gold',  sub: 'Satsang · Bhajan · Discourse' },
];
/** r7.7 D1 — the six mood tiles on the home page (the full 22 live under /m). */
const HOME_MOODS = [
  { slug: 'tonight',      label: 'Tonight',        glyph: '☾' },
  { slug: 'this-weekend', label: 'This weekend',   glyph: '◐' },
  { slug: 'date-night',   label: 'Date night',     glyph: '♡' },
  { slug: 'family-day',   label: 'Family day out', glyph: '⚘' },
  { slug: 'under-500',    label: 'Under ₹500',     glyph: '₹' },
  { slug: 'free',         label: 'Free',           glyph: '✧' },
];
export const MOOD_COUNT = 22;


const QUICK_CHIPS = [
  { value: 'all',           label: 'All' },
  { value: 'this-weekend',  label: 'This weekend' },
  { value: 'under-500',     label: 'Under ₹500' },
  { value: 'free',          label: 'Free' },
  { value: 'near-me',       label: 'Near me' },
  { value: 'family',        label: 'Family-friendly' },
  { value: 'indie',         label: 'Indie & live' },
  { value: 'late-night',    label: 'Late night' },
];

export default function HomePage() {
  const { t } = useTranslation();
  const { categories } = useCategories();   // r7.6.4
  const navigate = useNavigate();
  const { city } = useCity();
  const [params] = useSearchParams();
  const [chip, setChip] = useState(params.get('filter') || 'all');

  const [hero,        setHero]       = useState([]);
  const [trending,    setTrending]   = useState([]);
  const [curated,     setCurated]    = useState([]);
  const [counts,      setCounts]     = useState(null);   // r7.7 D1: live counts per vertical / mood
  const { user } = useAuth();                                   // r7.2
  const [communityFeed, setCommunityFeed] = useState([]);       // r7.2

  // ── Fetch homepage payload ────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Single endpoint that returns everything for the home shell
        const { data } = await api.get('/events/home', {
          params: { city: city || 'bengaluru', filter: chip },
        });
        if (cancelled) return;
        setHero(data.hero?.slides?.length ? adaptHeroSlides(data.hero.slides) : (data.hero_slides || []));   // r7.7 C4
        setCounts(data.counts || null);
        setTrending(data.trending || []);
        setCurated(data.curated || []);
      } catch (err) {
        // Graceful fallback: pull rails individually
        try {
          const [tr, cu] = await Promise.all([
            api.get('/events/trending', { params: { city, limit: 12 } }),
            api.get('/events/curated',  { params: { city, limit: 8 }  }),
          ]);
          if (cancelled) return;
          setTrending(tr.data?.events || []);
          setCurated(cu.data?.events || []);
        } catch { /* render empty rails */ }
      }
    })();
    return () => { cancelled = true; };
  }, [city, chip]);

  // r7.2 — events from communities the user follows
  useEffect(() => {
    let cancelled = false;
    if (!user) { setCommunityFeed([]); return () => {}; }
    (async () => {
      try {
        const { data } = await api.get('/me/community-feed', { params: { limit: 12 } });
        if (!cancelled) setCommunityFeed(data.events || []);
      } catch { /* rail simply doesn't render */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const heroSlides = useMemo(() => {
    if (hero.length) return hero;
    // First-paint placeholder if API hasn't returned yet
    return [{
      id: 'placeholder',
      eyebrow: 'Featured',
      title: 'Showz ',
      title_accent: 'happen.',
      lede: 'Discover live events around you. Concerts, comedy, theatre and more.',
      glyph: 'ॐ',
      variant: 'gold',
      cta_to: '/v/live-events',
      cta_label: 'Explore',
    }];
  }, [hero]);

  return (
    <div className="snt-home">
      {/* 1. Hero carousel */}
      <HeroCarousel slides={heroSlides} />

      {/* 2. Chip filter row */}
      <ChipFilterRow
        chips={QUICK_CHIPS}
        active={chip}
        onChange={setChip}
        sticky
      />

      {/* 3. Trending rail */}
      <EventRail
        events={trending}
        title={t('home.trending', 'Trending in')}
        titleAccent={city || 'Bengaluru'}
        viewAllTo={`/v/live-events?city=${encodeURIComponent(city || 'bengaluru')}`}
      />

      {/* 3b. r7.2 — communities you follow */}
      {communityFeed.length > 0 && (
        <EventRail
          events={communityFeed}
          title={t('community.feed_title', 'From communities you follow')}
        />
      )}

      {/* 4. Sponsored strip */}
      <SponsoredStrip slot="home_below_trending" />

      {/* r7.6.4 — Explore by category (the marketing taxonomy: 13 flowers) */}
      <section className="px-9 pt-8 pb-4" data-testid="home-categories">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-serif font-light" style={{ fontSize: 30, lineHeight: 1.2, letterSpacing: '-0.015em', color: 'var(--snt-text)' }}>
            {t('home.explore_cat', 'Pick your kind of ')}<span className="snt-em-accent">{t('home.explore_cat_accent', 'evening.')}</span>
          </h2>
          <Link to="/c" className="text-[12px] no-underline" style={{ color: 'var(--snt-text-3)' }}>{t('home.all_categories', 'All categories →')}</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {categories.slice(0, 12).map((c) => <CategoryCard key={c.slug} cat={c} compact />)}
        </div>
      </section>

      {/* 5. Browse by category — six vertical cards with the live count in the city (r7.7 D1; heading fixed — SNT-014) */}
      <section className="px-9 pt-8 pb-12" data-testid="home-categories">
        <div className="flex items-baseline justify-between mb-7 gap-4 flex-wrap">
          <h2
            className="font-serif font-light"
            style={{ fontSize: 30, lineHeight: 1.2, letterSpacing: '-0.015em', color: 'var(--snt-text)' }}
          >
            {t('home.browse_category_1', 'Browse by ')}
            <span className="snt-em-accent">{t('home.browse_category_2', 'category.')}</span>
          </h2>
          <Link to="/c" className="text-[12px] no-underline" style={{ color: 'var(--snt-text-2)' }} data-testid="home-all-verticals">
            {t('home.all_verticals', 'All 6 verticals · {{n}} sub-categories →', { n: counts?.sub_categories ?? '—' })}
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {VERTICALS.map((v) => {
            const n = counts?.verticals?.[v.slug];
            return (
              <Link
                key={v.slug}
                to={`/v/${v.slug}`}
                data-testid="home-vertical-card"
                className={`snt-art-${v.variant} aspect-[16/9] rounded-card no-underline flex flex-col justify-end p-5 transition-transform hover:scale-[1.01] relative overflow-hidden group`}
              >
                <span aria-hidden="true" className="absolute font-serif" style={{ fontSize: 'clamp(80px, 14vw, 140px)', opacity: 0.12, color: `var(--snt-category-${variantIdx(v.variant)})`, right: 18, top: -10, lineHeight: 1 }}>
                  {v.glyph}
                </span>
                <span className="snt-eyebrow mb-1.5 z-10">{t(`home.vertical_sub_${v.slug}`, v.sub)}</span>
                <span className="font-serif z-10" style={{ fontSize: 24, letterSpacing: '-0.02em', color: 'var(--snt-text)', lineHeight: 1.1 }}>
                  {t(`home.vertical_${v.slug}`, v.label)}
                </span>
                <span className="text-[11px] z-10 mt-1" style={{ color: 'var(--snt-text-3)' }} data-testid="home-vertical-count">
                  {n == null ? '' : n === 0 ? t('home.count_none', 'Nothing listed yet') : t('home.count_live', { count: n, city: counts?.city || t('home.everywhere', 'everywhere'), defaultValue: '{{count}} live in {{city}}' })}
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* 6. Curated rail */}
      {curated.length > 0 && (
        <EventRail
          events={curated}
          title={t('home.curated', 'Hand-picked')}
          titleAccent="this week."
          viewAllTo="/curated/this-week"
        />
      )}

      {/* 7. Or, browse by mood — six tiles with live counts; the 22 curated lists live under /m (r7.7 D1) */}
      <section className="px-9 pt-8 pb-16" data-testid="home-moods">
        <div className="flex items-baseline justify-between mb-7 gap-4 flex-wrap">
          <h2
            className="font-serif font-light"
            style={{ fontSize: 30, lineHeight: 1.2, letterSpacing: '-0.015em', color: 'var(--snt-text)' }}
          >
            {t('home.browse_mood_1', 'Or, browse by ')}
            <span className="snt-em-accent">{t('home.browse_mood_2', 'mood.')}</span>
          </h2>
          <Link to="/m" className="text-[12px] no-underline" style={{ color: 'var(--snt-text-2)' }} data-testid="home-all-moods">
            {t('home.all_moods', '{{n}} curated lists →', { n: MOOD_COUNT })}
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {HOME_MOODS.map((m) => {
            const n = counts?.moods?.[m.slug];
            return (
              <Link
                key={m.slug}
                to={`/m/${m.slug}`}
                data-testid="home-mood-tile"
                className="block no-underline rounded-card border border-line p-4 transition-colors hover:border-accent group"
                style={{ background: 'var(--snt-bg-1)' }}
              >
                <div className="flex items-baseline gap-2">
                  <span aria-hidden="true" className="font-serif text-[20px]" style={{ color: 'var(--snt-accent)' }}>{m.glyph}</span>
                  <span className="text-[15px] text-text group-hover:text-accent transition-colors font-serif">{t(`home.mood_${m.slug}`, m.label)}</span>
                </div>
                <div className="text-[11px] text-text-3 mt-1" data-testid="home-mood-count">
                  {n == null ? t('home.explore', 'Explore') + ' →' : n === 0 ? t('home.count_none', 'Nothing listed yet') : t('home.count_short', { count: n, defaultValue: '{{count}} live' })}
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* 8. Ask S panel */}
      <section className="px-9 pb-16">
        <div
          className="rounded-frame border border-line p-9 lg:p-11 grid lg:grid-cols-[1fr_1.5fr] gap-10 items-center"
          style={{ background: 'var(--snt-bg-2)' }}
        >
          <div>
            <SMark size="lg" glow />
            <h3
              className="font-serif font-light mt-5 mb-3"
              style={{
                fontSize: 32,
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                color: 'var(--snt-text)',
              }}
            >
              {t('home.ask_title_1', 'Tell ')}
              <span className="snt-em-accent">S</span>
              {t('home.ask_title_2', ' what you\'re in the mood for.')}
            </h3>
            <p className="text-[13px] leading-relaxed" style={{ color: 'var(--snt-text-2)' }}>
              {t('home.ask_lede', 'Conversational search. Ask in plain language — "comedy near me this weekend under ₹500" or "something quiet and meaningful for tonight" — and S finds it.')}
            </p>
          </div>
          <div>
            <button
              type="button"
              onClick={() => navigate('/ask')}
              className="w-full text-left snt-input flex justify-between items-center group transition-colors"
              style={{ padding: '18px 22px', borderRadius: 12, fontSize: 14 }}
              aria-label={t('home.ask_input_aria', 'Open AI assistant')}
            >
              <span style={{ color: 'var(--snt-text-3)' }}>
                {t('home.ask_placeholder', '"Tell me what kind of evening you\'re looking for…"')}
              </span>
              <span
                className="w-9 h-9 rounded-full flex items-center justify-center text-[14px]"
                style={{
                  background: 'var(--snt-accent)',
                  color: 'var(--snt-accent-fg)',
                }}
              >
                →
              </span>
            </button>
            <div className="flex flex-wrap gap-2 mt-4">
              {[
                'Comedy this weekend',
                'Family-friendly Sunday',
                'Quiet evening, classical',
                'Under ₹500 near me',
              ].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => navigate(`/ask?q=${encodeURIComponent(s)}`)}
                  className="snt-chip"
                  style={{ fontSize: 11, padding: '6px 12px' }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function variantIdx(v) {
  return { gold: 1, slate: 2, rose: 3, sage: 4, plum: 5 }[v] || 1;
}
